import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/api/client';
import { useToastStore } from '@/stores/toasts';
import { useAuthStore } from '@/stores/auth';

// Notification center (Onboarding v2 P2). Mirrors the API contract live in
// the controlplane:
//   GET  /v1/api/notifications              -> { items, unread }
//   POST /v1/api/notifications/{id}/read    -> { ok }
//   POST /v1/api/notifications/{id}/dismiss -> { ok }
//   POST /v1/api/notifications/read-all      -> { updated }
//
// The bell mounts once globally (in AppNav) so the badge is present on
// every surface; it polls every 60s while mounted. The badge can seed
// from /me's `onboarding.unread_notifications` so it renders instantly,
// before the first fetch lands.

export type NotificationSeverity = 'info' | 'success' | 'warning';
export type NotificationStatus = 'unread' | 'read' | 'dismissed';

export interface NotificationCta {
  label?: string;
  href?: string;
}

export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  cta: NotificationCta | null;
  severity: NotificationSeverity;
  status: NotificationStatus;
  created_at: string;
}

interface NotificationsResp {
  items: Notification[];
  unread: number;
}

const POLL_INTERVAL_MS = 60_000;

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<Notification[]>([]);
  // Seed from /me so the badge is correct before the first fetch resolves.
  const unread = ref<number>(
    useAuthStore().user?.onboarding?.unread_notifications ?? 0,
  );
  const loading = ref(false);
  const error = ref<string | null>(null);
  const open = ref(false);

  // Single shared interval handle; guarded so repeated startPolling() calls
  // (e.g. a remount) never stack timers.
  let pollTimer: number | null = null;

  async function fetch(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const resp = await api.get<NotificationsResp>('/v1/api/notifications');
      items.value = resp.items;
      unread.value = resp.unread;
    } catch (e) {
      error.value = (e as { error?: string }).error ?? 'Failed to load notifications';
    } finally {
      loading.value = false;
    }
  }

  function decrementUnread(): void {
    unread.value = Math.max(0, unread.value - 1);
  }

  async function markRead(id: string): Promise<void> {
    const n = items.value.find((x) => x.id === id);
    if (!n || n.status !== 'unread') return; // nothing to do
    n.status = 'read';
    decrementUnread();
    try {
      await api.post(`/v1/api/notifications/${id}/read`);
    } catch (e) {
      // Roll back the optimistic flip.
      n.status = 'unread';
      unread.value += 1;
      useToastStore().error(
        (e as { error?: string }).error ?? 'Could not mark as read',
      );
    }
  }

  async function dismiss(id: string): Promise<void> {
    const idx = items.value.findIndex((x) => x.id === id);
    const removed = idx < 0 ? undefined : items.value[idx];
    if (!removed) return;
    items.value.splice(idx, 1);
    const wasUnread = removed.status === 'unread';
    if (wasUnread) decrementUnread();
    try {
      await api.post(`/v1/api/notifications/${id}/dismiss`);
    } catch (e) {
      // Restore the row at its original position.
      items.value.splice(idx, 0, removed);
      if (wasUnread) unread.value += 1;
      useToastStore().error(
        (e as { error?: string }).error ?? 'Could not dismiss notification',
      );
    }
  }

  async function markAllRead(): Promise<void> {
    if (unread.value === 0) return;
    // Snapshot the ids we flip so a failure can re-sync from the server
    // rather than relying on a parallel-array index.
    const flipped = items.value.filter((n) => n.status === 'unread');
    flipped.forEach((n) => { n.status = 'read'; });
    unread.value = 0;
    try {
      await api.post('/v1/api/notifications/read-all');
      useToastStore().success('All notifications marked read');
    } catch (e) {
      // Re-sync authoritative state from the server (one bulk call).
      await fetch();
      useToastStore().error(
        (e as { error?: string }).error ?? 'Could not mark all read',
      );
    }
  }

  function startPolling(): void {
    if (pollTimer !== null) return; // already polling
    pollTimer = window.setInterval(() => { void fetch(); }, POLL_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (pollTimer === null) return;
    window.clearInterval(pollTimer);
    pollTimer = null;
  }

  function openPanel(): void { open.value = true; void fetch(); }
  function closePanel(): void { open.value = false; }
  function toggle(): void {
    if (open.value) closePanel();
    else openPanel();
  }

  return {
    items, unread, loading, error, open,
    fetch, markRead, dismiss, markAllRead,
    startPolling, stopPolling,
    openPanel, closePanel, toggle,
  };
});
